import DOMPurify from 'dompurify';
import stockData from '../data/stock_symbols.json';

// ── Stock symbols database (loaded synchronously via bundler) ──
// No async fetch = no race condition = no window where fake tickers are accepted
const _stockSymbols = stockData.symbols;

/**
 * Check if a symbol is a valid stock ticker.
 * Returns { valid, name, exchange } or { valid: false }.
 */
export function isValidTicker(symbol) {
    const entry = _stockSymbols[symbol.toUpperCase()];
    if (entry) return { valid: true, name: entry.name, exchange: entry.exchange };
    return { valid: false };
}

/**
 * Search stock symbols for autocomplete.
 * Matches by symbol prefix or company name substring.
 * Returns array of { symbol, name, exchange }.
 */
export function searchStockSymbols(query, limit = 8) {
    if (!_stockSymbols || !query) return [];
    const q = query.toUpperCase();
    const ql = query.toLowerCase();
    const results = [];

    for (const [symbol, data] of Object.entries(_stockSymbols)) {
        if (symbol.startsWith(q) || data.name.toLowerCase().includes(ql)) {
            results.push({ symbol, name: data.name, exchange: data.exchange });
            if (results.length >= limit) break;
        }
    }

    results.sort((a, b) => {
        const aStartsWith = a.symbol.startsWith(q);
        const bStartsWith = b.symbol.startsWith(q);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return a.symbol.length - b.symbol.length;
    });

    return results;
}

// Posts are authored with ReactQuill, so the input is already rich HTML
// (paragraphs, lists, bold, italic, underline, blockquotes, headings, links).
// Allow that whole vocabulary instead of stripping to a markdown-only subset.
const PURIFY_CONFIG = {
    ALLOWED_TAGS: [
        'p', 'br', 'span', 'div',
        'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'sub', 'sup',
        'ol', 'ul', 'li',
        'blockquote', 'pre', 'code',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'a',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'dir', 'title'],
    KEEP_CONTENT: true,
};

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
        const href = node.getAttribute('href') || '';
        if (!/^https?:\/\//i.test(href)) {
            node.removeAttribute('href');
        }
    }
});

function escapeHtml(str) {
    if (!str) return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

const CASHTAG_RE = /(?<![a-zA-Z0-9/])\$([A-Z]{1,5})(?![a-zA-Z])/g;
const URL_RE = /https?:\/\/[^\s<>"')]+/g;

// Walk a parsed DOM tree and convert cashtags / bare URLs in text nodes to
// anchor tags. Skip text inside existing <a> elements so we don't nest links.
function autolinkTextNodes(root) {
    const stack = [root];
    while (stack.length) {
        const node = stack.pop();
        if (!node || !node.childNodes) continue;
        // Snapshot — we may replace children mid-iteration.
        const children = Array.from(node.childNodes);
        for (const child of children) {
            if (child.nodeType === 3 /* TEXT_NODE */) {
                const fragment = buildLinkedFragment(child.textContent);
                if (fragment) child.parentNode.replaceChild(fragment, child);
            } else if (child.nodeType === 1 /* ELEMENT_NODE */) {
                if (child.tagName === 'A') continue;
                stack.push(child);
            }
        }
    }
}

function buildLinkedFragment(text) {
    if (!text) return null;
    CASHTAG_RE.lastIndex = 0;
    URL_RE.lastIndex = 0;
    if (!CASHTAG_RE.test(text) && !URL_RE.test(text)) return null;
    CASHTAG_RE.lastIndex = 0;
    URL_RE.lastIndex = 0;

    let html = escapeHtml(text);

    html = html.replace(CASHTAG_RE, (match, symbol) => {
        const check = isValidTicker(symbol);
        if (!check.valid) return match;
        const exchange = check.exchange === 'CRYPTO' ? '' : `:${check.exchange}`;
        const href = `https://www.google.com/finance/quote/${symbol}${exchange}`;
        return `<a href="${href}" class="post-link post-link--cashtag" target="_blank" rel="noopener noreferrer">$${symbol}</a>`;
    });

    html = html.replace(URL_RE, (url) => {
        return `<a href="${url}" class="post-link" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });

    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    return tpl.content;
}

/**
 * Render a post's details as safe HTML.
 *
 * Posts are authored with ReactQuill, so the stored value is HTML.
 * We sanitize it (allowing rich-text tags) and additionally auto-link bare
 * URLs and validated stock cashtags ($AAPL) found inside text nodes.
 *
 * Name kept as `renderMarkdown` for backward compatibility with the many
 * callsites already importing it.
 */
export function renderMarkdown(text) {
    if (!text) return '';

    // Sanitize first so we walk a trustworthy DOM (no scripts/handlers).
    const safeHtml = DOMPurify.sanitize(text, PURIFY_CONFIG);

    // DOMParser is browser-only; fall back to the sanitized HTML in non-DOM envs.
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        return safeHtml;
    }

    const doc = new window.DOMParser().parseFromString(
        `<div>${safeHtml}</div>`,
        'text/html'
    );
    const wrapper = doc.body.firstChild;
    if (!wrapper) return safeHtml;

    autolinkTextNodes(wrapper);

    // Sanitize again so freshly inserted anchors pass through the same
    // hardening (target/rel enforcement, href scheme check).
    return DOMPurify.sanitize(wrapper.innerHTML, PURIFY_CONFIG);
}

export function markdownProps(text) {
    return {
        dangerouslySetInnerHTML: { __html: renderMarkdown(text) },
    };
}

/**
 * Strip HTML to plain text (sanitized first, then tags removed).
 * Use for compact contexts like notification rows or fullscreen captions
 * where we want a one-line preview without raw `<p>` / `<strong>` markup.
 */
export function htmlToPlainText(text) {
    if (!text) return '';
    // Sanitize first so any malicious markup is dropped before we touch it.
    const safeHtml = DOMPurify.sanitize(text, PURIFY_CONFIG);
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        // SSR / non-DOM fallback: regex strip.
        return safeHtml
            .replace(/<\s*br\s*\/?\s*>/gi, ' ')
            .replace(/<\/(p|div|li|ul|ol|h[1-6]|blockquote)\s*>/gi, ' ')
            .replace(/<[^>]+>/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    }
    const doc = new window.DOMParser().parseFromString(
        `<div>${safeHtml}</div>`,
        'text/html'
    );
    const txt = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    return txt;
}
